import { FunctionComponent } from "react";

import { arrayMove } from "@dnd-kit/sortable";

import Item from "@/components/SortableList/Item";
import { TItem, SortableList } from "@/components/SortableList/SortableList";
import { Toggle } from "@/components/Toggle/Toggle";
import { Modal } from "@/components";

interface LinkedinConnectionsColumnModalProps {
	setShowSelectColumns: (value: boolean) => void;
	showSelectColumns: boolean;
	tableConfig: TItem[];
	setTableConfig: (value: TItem[]) => void;
	handleResetToDefault: () => void;
}

export const LinkedinConnectionsColumnModal: FunctionComponent<
	LinkedinConnectionsColumnModalProps
> = ({
	setShowSelectColumns,
	showSelectColumns,
	tableConfig,
	setTableConfig,
	handleResetToDefault,
}) => {
	return (
		<Modal
			clearButtonText="Reset to default"
			handleClear={() => handleResetToDefault()}
			handleClose={() => setShowSelectColumns(false)}
			handleSave={() => setShowSelectColumns(false)}
			isOpened={showSelectColumns}
			size="md"
			submitButtonColor="BRAND"
			submitButtonText="Close"
			takeFocus
			title="Columns"
		>
			<div className="mb-6 flex flex-row items-center">
				<Toggle
					className="mr-3"
					isChecked={tableConfig.filter((c) => !c.locked).every((c) => c.show)}
					onChange={() => {
						const newShow = !tableConfig.filter((c) => !c.locked).every((c) => c.show);

						setTableConfig(
							tableConfig.map((c) => {
								if (!c.locked) {
									c.show = newShow;
								}

								return c;
							})
						);
					}}
				/>
				<div className="text-ssm">Display all columns</div>
			</div>

			{tableConfig
				.filter((c) => c.allowExclude && !c.moveable)
				.map((c, index) => (
					<Item
						key={`locked-${c.uid}-${index}`}
						handleShowToggle={(uid) => {
							const item = tableConfig.find((c) => c.uid === uid);

							if (item) {
								item.show = !(typeof item.show !== "undefined" ? item.show : true);
								const idx = tableConfig.findIndex((c) => c.uid === uid);

								setTableConfig([...tableConfig.slice(0, idx), item, ...tableConfig.slice(idx + 1)]);
							}
						}}
						handleStickyToggle={(uid) => {
							const index = tableConfig.findIndex((c) => c.uid === uid);

							if (index !== -1) {
								const updatedItem = { ...tableConfig[index], locked: !tableConfig[index].locked };

								setTableConfig([
									...tableConfig.slice(0, index),
									updatedItem,
									...tableConfig.slice(index + 1),
								]);
							}
						}}
						item={
							{
								id: index + 1,
								content: c.content,
								locked: c.locked,
								show: c.show,
								uid: c.uid,
								moveable: c.moveable,
								hideable: c.hideable,
							} as TItem
						}
					/>
				))}

			<SortableList
				defaultItems={tableConfig
					?.filter((c) => c.moveable)
					.map((c, index) => ({
						...c,
						id: index + 1,
						content: c.content,
						locked: c.locked,
						show: c.show,
						uid: c.uid,
						moveable: c.moveable,
						hideable: c.hideable,
					}))}
				handleShowToggle={(uid) => {
					const item = tableConfig.find((c) => c.uid === uid);

					if (item) {
						item.show = !(typeof item.show !== "undefined" ? item.show : true);
						const index = tableConfig.findIndex((c) => c.uid === uid);

						setTableConfig([...tableConfig.slice(0, index), item, ...tableConfig.slice(index + 1)]);
					}
				}}
				handleStickyToggle={(uid) => {
					const index = tableConfig.findIndex((c) => c.uid === uid);

					if (index !== -1) {
						const updatedItem = { ...tableConfig[index], locked: !tableConfig[index].locked };

						setTableConfig([
							...tableConfig.slice(0, index),
							updatedItem,
							...tableConfig.slice(index + 1),
						]);
					}
				}}
				handleSwitchColumns={(fromUid, toUid) => {
					const movedItemIndex = tableConfig.findIndex((c) => c.uid === fromUid);
					const destinationItemIndex = tableConfig.findIndex((c) => c.uid === toUid);
					const newTableConfig = arrayMove(tableConfig, movedItemIndex, destinationItemIndex);

					setTableConfig([...newTableConfig]);
				}}
			/>
		</Modal>
	);
};
